import { useState, useEffect } from 'react'
import * as XLSX from 'xlsx'
import { useParams, useNavigate } from 'react-router-dom'
import { get } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import { Icon } from '@iconify/react'
import {
  WeightDiscrepancyInvoice as InvoiceType,
  WeightDiscrepancyItem,
} from './types'
import SendInvoiceModal from './modals/SendInvoiceModal'
import MarkPaidModal from './modals/MarkPaidModal'
import ExtendDeadlineModal from './modals/ExtendDeadlineModal'
import './WeightDiscrepancy.scss'

function WeightDiscrepancyInvoice() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { notify, profile } = useGlobalContext()
  const [invoice, setInvoice] = useState<InvoiceType | null>(null)
  const [loading, setLoading] = useState(true)

  // Modal states
  const [showSendModal, setShowSendModal] = useState(false)
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false)
  const [showExtendModal, setShowExtendModal] = useState(false)

  function fetchInvoice() {
    setLoading(true)
    get(`admin/weight-discrepancy/invoices/${id}`)
      .then((result: any) => {
        if (result.status === 200) {
          setInvoice(result.data)
        } else {
          notify('error', result.message)
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        notify('error', 'Failed to fetch invoice')
      })
  }

  useEffect(() => {
    if (id) {
      fetchInvoice()
    }
  }, [id])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-NG', {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <span className='badge badge-default'>Draft</span>
      case 'sent':
        return <span className='badge badge-info'>Sent</span>
      case 'paid':
        return <span className='badge badge-success'>Paid</span>
      case 'overdue':
        return <span className='badge badge-danger'>Overdue</span>
      default:
        return <span className='badge badge-default'>{status}</span>
    }
  }

  const exportInvoiceItems = () => {
    if (!invoice || !invoice.items || invoice.items.length === 0) return

    const data = invoice.items.map((item, index) => ({
      'S/N': index + 1,
      'Tracking ID': item.trackingId,
      'Old Weight': `${item.oldWeight} kg`,
      'New Weight': `${item.newWeight} kg`,
      Difference: `${(item.newWeight - item.oldWeight).toFixed(2)} kg`,
      Amount: item.amount,
      Description: item.description || '-',
      Added: formatDate(item.createdAt),
    }))

    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Invoice Items')
    XLSX.writeFile(wb, `invoice_${invoice.invoiceNumber || 'draft'}.xlsx`)
  }

  const handleModalSuccess = () => {
    fetchInvoice()
  }

  if (loading) {
    return (
      <div className='home'>
        <FormLoader position='center' />
      </div>
    )
  }

  if (!invoice) {
    return (
      <div className='home'>
        <div className='empty flex justify-center'>
          <div className='empty-cover'>
            <Icon icon='mdi:file-document-alert' />
            <h1>Invoice Not Found</h1>
            <button
              className='btn-primary'
              onClick={() => navigate('/admin/weight-discrepancy')}
              style={{
                marginTop: '20px',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                backgroundColor: '#446ce4',
                color: '#fff',
              }}
            >
              Back to List
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='home'>
      <div className='home_tag flex justify-between'>
        <h2>
          <span
            onClick={() => navigate('/admin/weight-discrepancy')}
            style={{ cursor: 'pointer', color: '#446ce4' }}
          >
            Weight Discrepancy
          </span>{' '}
          / Invoice Details
        </h2>
      </div>

      {/* Invoice Header */}
      <div className='invoice-header'>
        <div className='invoice-info'>
          <h2>
            {invoice.invoiceNumber || 'Draft Invoice'}
            {getStatusBadge(invoice.status)}
          </h2>
          <p>
            <strong>Customer:</strong>{' '}
            <span
              className='SI'
              onClick={() => {
                if (invoice.userModel === 'User') {
                  navigate('/admin/customer/' + invoice.userId)
                } else {
                  navigate('/admin/business/' + invoice.userId)
                }
              }}
            >
              Visit
            </span>
          </p>
          <p>
            <strong>Type:</strong> {invoice.userModel}
          </p>
        </div>

        <div className='summary-stats'>
          <div className='stat-card'>
            <h4>Total Amount</h4>
            <p className='total'>{formatCurrency(invoice.totalAmount)}</p>
          </div>
          <div className='stat-card'>
            <h4>Amount Paid</h4>
            <p className='success'>{formatCurrency(invoice.amountPaid)}</p>
          </div>
          <div className='stat-card'>
            <h4>Balance</h4>
            <p className={invoice.status === 'overdue' ? 'overdue' : 'pending'}>
              {formatCurrency(invoice.totalAmount - invoice.amountPaid)}
            </p>
          </div>
          <div className='stat-card'>
            <h4>Items</h4>
            <p>{invoice.items.length}</p>
          </div>
        </div>

        {/* Timeline Info */}
        <div className='summary-stats' style={{ marginTop: '15px' }}>
          <div className='stat-card'>
            <h4>Created</h4>
            <p style={{ fontSize: '14px' }}>{formatDate(invoice.createdAt)}</p>
          </div>
          {invoice.sentAt && (
            <div className='stat-card'>
              <h4>Sent</h4>
              <p style={{ fontSize: '14px' }}>{formatDate(invoice.sentAt)}</p>
            </div>
          )}
          {invoice.deadline && (
            <div className='stat-card'>
              <h4>Deadline</h4>
              <p
                style={{ fontSize: '14px' }}
                className={invoice.status === 'overdue' ? 'overdue' : ''}
              >
                {formatDate(invoice.deadline)}
              </p>
            </div>
          )}
          {invoice.paidAt && (
            <div className='stat-card'>
              <h4>Paid</h4>
              <p style={{ fontSize: '14px' }}>{formatDate(invoice.paidAt)}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className='invoice-actions'>
          {invoice.status === 'draft' && (
            <button
              className='btn-primary'
              onClick={() => setShowSendModal(true)}
            >
              <Icon icon='mdi:send' />
              Send Invoice
            </button>
          )}

          {(invoice.status === 'sent' || invoice.status === 'overdue') && (
            <>
              <button
                className='btn-success'
                onClick={() => setShowMarkPaidModal(true)}
              >
                <Icon icon='mdi:check-circle' />
                Mark as Paid
              </button>
              {profile.role === 'super admin' && (
                <button
                  className='btn-warning'
                  onClick={() => setShowExtendModal(true)}
                >
                  <Icon icon='mdi:calendar-plus' />
                  Extend Deadline
                </button>
              )}
            </>
          )}

          {invoice.invoiceUrl && (
            <button
              className='btn-primary'
              onClick={() => window.open(invoice.invoiceUrl, '_blank')}
            >
              <Icon icon='mdi:file-pdf-box' />
              View PDF
            </button>
          )}
        </div>
      </div>

      {/* Items Section */}
      <div className='items-section'>
        <div className='flex justify-between items-center mb-4'>
          <h3>Discrepancy Items</h3>
          <button
            onClick={exportInvoiceItems}
            className='btn-secondary'
            style={{
              fontSize: '13px',
              padding: '6px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
            }}
          >
            <Icon icon='mdi:download-outline' /> Export Items
          </button>
        </div>

        {/* Mobile Cards */}
        <div className='mobile-table'>
          {invoice.items.map((item: WeightDiscrepancyItem, i: number) => (
            <div className='card' key={i}>
              <div className='flex justify-between row'>
                <div className='count flex items-center justify-center'>
                  {i + 1}
                </div>
              </div>
              <div className='row flex justify-between'>
                <h4>Tracking ID</h4>
                <p>{item.trackingId}</p>
              </div>
              <div className='row flex justify-between'>
                <h4>Old Weight</h4>
                <p>{item.oldWeight} kg</p>
              </div>
              <div className='row flex justify-between'>
                <h4>New Weight</h4>
                <p>{item.newWeight} kg</p>
              </div>
              <div className='row flex justify-between'>
                <h4>Difference</h4>
                <p>{(item.newWeight - item.oldWeight).toFixed(2)} kg</p>
              </div>
              <div className='row flex justify-between'>
                <h4>Amount</h4>
                <p>{formatCurrency(item.amount)}</p>
              </div>
              {item.description && (
                <div className='row flex justify-between'>
                  <h4>Description</h4>
                  <p>{item.description}</p>
                </div>
              )}
              <div className='row flex justify-between'>
                <h4>Added</h4>
                <p>{formatDate(item.createdAt)}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table */}
        <table className='desktop-table'>
          <thead>
            <tr>
              <th>S/N</th>
              <th>Tracking ID</th>
              <th>Old Weight</th>
              <th>New Weight</th>
              <th>Difference</th>
              <th>Amount</th>
              <th>Description</th>
              <th>Added</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item: WeightDiscrepancyItem, i: number) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{item.trackingId}</td>
                <td>{item.oldWeight} kg</td>
                <td>{item.newWeight} kg</td>
                <td>{(item.newWeight - item.oldWeight).toFixed(2)} kg</td>
                <td>{formatCurrency(item.amount)}</td>
                <td>{item.description || '-'}</td>
                <td>{formatDate(item.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {showSendModal && (
        <SendInvoiceModal
          invoiceId={invoice._id}
          totalAmount={invoice.totalAmount}
          itemCount={invoice.items.length}
          onClose={() => setShowSendModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {showMarkPaidModal && (
        <MarkPaidModal
          invoiceId={invoice._id}
          totalAmount={invoice.totalAmount}
          onClose={() => setShowMarkPaidModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {showExtendModal && (
        <ExtendDeadlineModal
          invoiceId={invoice._id}
          currentDeadline={invoice.deadline || ''}
          onClose={() => setShowExtendModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  )
}

export default WeightDiscrepancyInvoice
